import { CommonModule, Location } from '@angular/common';
import { Component, Input, OnInit, inject, signal } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { provideMomentDateAdapter } from '@angular/material-moment-adapter';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatNativeDateModule, MatOptionModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { Router, RouterModule } from '@angular/router';
import { Patient } from '../../../services/model';
import { PatientsService } from '../../../services/patients.service';

// Depending on whether rollup is used, moment needs to be imported differently.
// Since Moment.js doesn't have a default export, we normally need to import using the `* as`
// syntax. However, rollup creates a synthetic default module and we thus need to import it using
// the `default as` syntax.
import * as _moment from 'moment';
// tslint:disable-next-line:no-duplicate-imports
import { default as _rollupMoment } from 'moment';
import { NavigationComponent } from '../../../components/navigation/navigation.component';
import { LogService } from '../../../services/log.service';

const moment = _rollupMoment || _moment;

// See the Moment.js docs for the meaning of these formats:
// https://momentjs.com/docs/#/displaying/format/
export const MY_FORMATS = {
  parse: {
    dateInput: 'DD.MM.YYYY',
  },
  display: {
    dateInput: 'DD.MM.YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};
@Component({
  selector: 'app-patient-new',
  standalone: true,
  providers: [
    // Moment can be provided globally to your app by adding `provideMomentDateAdapter`
    // to your app config. We provide it at the component level here, due to limitations
    // of our example generation script.
    provideMomentDateAdapter(MY_FORMATS),
  ],
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatOptionModule,
    MatButtonModule,
    MatCardModule,
    ReactiveFormsModule,
    MatRadioModule,
    MatChipsModule,
    MatSelectModule,
    MatIconModule,
    RouterModule,
    MatDatepickerModule,
    MatNativeDateModule,
    NavigationComponent,
  ],
  templateUrl: './patient-new.component.html',
  styleUrl: './patient-new.component.scss',
})
export class PatientNewComponent implements OnInit {
  location: Location = inject(Location);
  $error = signal('');
  @Input() patientId?: string;

  public minDate: Date = new Date(1900, 0, 1);
  public maxDate = new Date();
  private router: Router = inject(Router);
  private fb: FormBuilder = inject(FormBuilder);
  // private matDateFormats: MatDateFormats = inject(MAT_DATE_FORMATS);
  // private matMoment: MatMomentDateAdapterOptions = inject(MAT_MOMENT_DATE_ADAPTER_OPTIONS);
  // private matDate: unknown = inject(MAT_DATE_LOCALE);
  private patientsService: PatientsService = inject(PatientsService);
  private logger: LogService = inject(LogService);

  patientForm: FormGroup = this.fb.group({
    name: [''],
    uid: ['', Validators.required],
    gender: ['', Validators.required],
    birthDate: ['', Validators.required],
    // birthYear: ['', [Validators.required, Validators.pattern(/^\d{4}$/)]],
    // birthMonth: ['', [Validators.required, Validators.min(1), Validators.max(12)]],
    diagnosis: [''],
    dominantSide: [''],
    affectedSide: [''],
  });

  ngOnInit(): void {
    console.log('input id ' + this.patientId);
    // console.log('matDateFormats  ', this.matDateFormats);
    // console.log('matDate  ', this.matDate);
    // console.log('matMoment  ', this.matMoment);
    if (this.patientId !== undefined) {
      this.patientsService.getPatient(this.patientId).then(patient => {
        // console.log('ngoninit', patient);
        this.logger.log('patient edit ' + patient._documentId);
        const patientFormData = { ...patient };
        delete patientFormData._documentId;
        let birthDate: Date;
        if (patientFormData.birthDateIso === undefined) {
          birthDate = new Date(patientFormData.birthYear!, patientFormData.birthMonth! - 1, 1);
        } else {
          // birthDate = new Date(patientFormData.birthDateIso);
          birthDate = new Date(patientFormData.birthYear!, patientFormData.birthMonth! - 1, patientFormData.birthDay);
        }
        delete patientFormData.birthDay;
        delete patientFormData.birthDateIso;
        delete patientFormData.birthMonth;
        delete patientFormData.birthYear;
        if (patientFormData.diagnosis === undefined) {
          patientFormData.diagnosis = '';
        }
        this.patientForm.setValue({ ...patientFormData, birthDate });
      });
    }
  }

  onNameBlur($event: any) {
    const nameString = $event.target.value;
    if (this.patientForm.controls['uid'].value.length === 0) {
      try {
        const short = this.generateInitials(nameString);
        this.patientForm.controls['uid'].setValue(short);
      } catch (error) {
        // ignore;
      }
    }
  }

  onDateBlur($event: any) {
    // const dateString = $event.target.value;
    // const parts = dateString.split('.');
    // const day = parseInt(parts[0], 10);
    // const month = parseInt(parts[1], 10) - 1; // Months are zero-based in JavaScript
    // const year = parseInt(parts[2], 10);
    // const parsedDate = new Date(year, month, day);
    // console.log('parsedDate' + parsedDate);
    // this.patientForm.controls['birthDate'].setValue(parsedDate);
  }

  createPatient(): void {
    if (this.patientForm.valid) {
      console.log(this.patientForm.value);
      const patient: Patient = this.getPatientDate();
      this.patientsService
        .createPatient(patient)
        .then(() => {
          this.router.navigate(['patients']);
        })
        .catch(error => {
          console.log('An error occurred', { ...error });
          this.$error.set(error.code);
        });
    } else {
      this.$error.set('Error: ' + this.getErrors());
    }
  }

  updatePatient(): void {
    console.log('this.updatePatient', this.patientForm);

    if (this.patientForm.valid) {
      console.log(this.patientForm.value);
      const patient: Patient = this.getPatientDate();
      // return;
      patient._documentId = this.patientId;
      this.patientsService
        .updatePatient(patient)
        .then(() => {
          // this.router.navigate(['patients']);
          this.location.back();
        })
        .catch(error => {
          console.log('An error occurred', { ...error });
          this.$error.set(error.code);
        });
    } else {
      console.log('errors ', this.patientForm.errors);

      this.$error.set('Error: ' + this.getErrors());
    }
  }

  getErrors(): string {
    let result = '';
    Object.keys(this.patientForm.controls).forEach(key => {
      const controlErrors = this.patientForm.get(key)?.errors;
      if (controlErrors) {
        result += JSON.stringify(controlErrors) + ' \n';
      }
    });
    return result;
  }

  getPatientDate(): Patient {
    console.log('this.patientForm.value.birthDate', this.patientForm.value.birthDate);

    const date: Date = this.patientForm.value.birthDate;
    const dateMoment: moment.Moment = moment(date);
    const patient: Patient = this.patientForm.value;
    patient.birthYear = dateMoment.get('year');
    patient.birthMonth = dateMoment.get('month') + 1;
    patient.birthDay = dateMoment.get('date');
    patient.birthDateIso = dateMoment.toISOString();
    console.log('patient: ', patient);
    const patientNew: Patient = {
      birthDateIso: patient.birthDateIso,
      birthDay: patient.birthDay,
      birthMonth: patient.birthMonth,
      birthYear: patient.birthYear,
      affectedSide: patient.affectedSide,
      diagnosis: patient.diagnosis,
      dominantSide: patient.dominantSide,
      gender: patient.gender,
      uid: patient.uid,
      name: patient.name,
    };
    if (patient._documentId) {
      patientNew._documentId = patient._documentId;
    }
    return patientNew;
  }

  private generateInitials(fullName: string) {
    return fullName
      .split(/[- ]/) // Trenne an Leerzeichen und Bindestrichen
      .map(word => word.charAt(0).toUpperCase() + (word.charAt(1)?.toLowerCase() || '')) // Erster Buchstabe groß, zweiter klein
      .join(''); // Zusammensetzen der Buchstaben
  }
}
